import React, { useState, useEffect } from 'react';
import { GetRequest } from 'components/common';
import MaterialForm from 'components/frank/MaterialForm.jsx';
import UserUnitPriceCreateForm from 'components/quote_builder/UserUnitPriceCreateForm.jsx';
import QuoteBuilderForm from 'components/quote_builder/QuoteBuilderForm.jsx';
import AddMachineFlow from 'components/frank/AddMachineFlow.jsx';
import { machineTabConfig } from 'components/frank/utils/machineSettings.js';

// The forms the guided setup wizard renders for its OWN-lines setup steps -- one per missing prerequisite, each a
// discrete counted stage (see setupWizardStages.js). A machine is TWO steps: MachineEntryStep adds the machine
// record, MachinePriceStep prices it. Completing any step calls onResolved -- the wizard's re-solve, which
// refreshes the counts AND forces the wizard to recompute -- so the now-satisfied step drops and the wizard
// advances. Completing a step IS how you move on, and the "Step X of N" total shrinks as prerequisites are met.

// ADD A MATERIAL PRICE. The list leads with the "frequently used" system materials (Seç ve fiyat ekle -> just
// enter your price), with the full create form for anything else -- the same material adder the inline empty
// state shows. No bobbin default is seeded here (the inline adder takes it from the live filter island): this
// material step comes BEFORE the line step where Bobin is chosen, so there is no section bobbin state yet, and
// the form uses its own default -- the owner sets Bobin on the material itself.
// Like the machine-price step, the form shows NO buttons of its own (hideButtons): while it is on the price LIST
// the wizard shows no Next (picking a material carries the step forward); once the owner opens a create/copy/edit
// sub-form the wizard's Next SAVES it (via submitRef) and its Back returns to the list (via cancelRef). The form
// reports which of those it is on through onFormStateChange so the wizard knows which footer to show.
export const MaterialPriceStep = ({ materialKey, boardThicknessMm, corrugatedCardboardFlute, submitRef, cancelRef, onSaveError, onFormStateChange, onResolved }) => (
  <MaterialForm
    materialKey={materialKey}
    boardThicknessMm={boardThicknessMm}
    corrugatedCardboardFlute={corrugatedCardboardFlute}
    systemItemsFirst
    afterSave={onResolved}
    hideButtons
    submitRef={submitRef}
    cancelRef={cancelRef}
    onSaveError={onSaveError}
    onShowingStateChange={onFormStateChange}
  />
);

// ADD THE MACHINE ITSELF (not its price -- that is the next step). Reuses AddMachineFlow in entryOnly mode: the
// curated library picker (or "enter manually") creates the machine and finishes the step, without falling into
// its price form. onMachineAdded re-solves: the refreshed state now holds the new machine as the section's
// unpriced machine, so this step drops and the wizard advances onto pricing it. Reusing AddMachineFlow keeps the
// pick/manual-entry flow in ONE place rather than a second copy here. libraryAttributeIds narrows the library to
// the machines for the section's part_type (Frank::SectionSetupRequirements), so the owner cannot pick one that
// leaves this step unsatisfied.
export const MachineEntryStep = ({ machineKey, allowedTypes, libraryAttributeIds, onMachineAdded }) => (
  <AddMachineFlow
    machineKey={machineKey}
    tabConfig={machineTabConfig(machineKey)}
    allowedTypes={allowedTypes}
    libraryAttributeIds={libraryAttributeIds}
    entryOnly
    onCopied={onMachineAdded}
    onPriced={() => {}}
    onClose={() => {}}
  />
);

// ADD THE MACHINE'S PRICE. The price form opens on a new record with the machine field pre-selected (via
// UserUnitPriceCreateForm's context) on attributeId -- the owner's unpriced machine for the section's part_type,
// whether they had it already (the machine-entry step was skipped) or just added it in the entry step. The wizard
// never renders this step without one (shownStageIndex), so the field's fallback -- auto-selecting the owner's
// first machine of this key, possibly one the section cannot use -- is never what opens here. The form shows NO
// buttons of its own (hideButtons) -- the wizard's Next saves it through submitRef, its Back cancels -- and on save
// onResolved re-solves, which drops this step and advances.
export const MachinePriceStep = ({ machineKey, attributeId, submitRef, onSaveError, onResolved }) => (
  <UserUnitPriceCreateForm
    priceKey={machineKey}
    contextKey={machineKey}
    attributeId={attributeId}
    afterSave={onResolved}
    hideButtons
    submitRef={submitRef}
    onSaveError={onSaveError}
  />
);

// The section's material thickness as the owner reads it -- "2.5" / "2,5" -- in the page's language.
const formatThicknessMm = (thicknessMm) => (
  Number(thicknessMm).toLocaleString(window.locale === 'tr' ? 'tr-TR' : 'en-US', { maximumFractionDigits: 2 })
);

// YOUR EXISTING MACHINES AREN'T SUITABLE. The owner has machines for the section's part_type, but none can run the
// section's material thickness, so none can make a manufacturing line here (the finder drops them on thickness).
// Says so, names each machine with its thickness range, and offers the two ways forward:
//   - "Add a new machine": the same flow as MachineEntryStep, whose library lists only machines that can run it;
//   - "Edit <machine>": that machine's own edit form -- the one the price form's Edit link opens (QuoteBuilderForm in
//     edit mode, which loads the machine's saved properties first) -- so the owner can widen its thickness range.
// Either finishes through onResolved, the wizard's re-solve: once a machine can run the material this step drops and
// the wizard moves on to pricing it (or past it, if it is priced).
export const MachineThicknessMismatchStep = ({
  machineKey, materialThicknessMm, machines, allowedTypes, libraryAttributeIds, onResolved,
}) => {
  const [adding, setAdding] = useState(false);
  const [editingAttributeId, setEditingAttributeId] = useState(null);
  const [machineConfig, setMachineConfig] = useState(null);

  useEffect(() => {
    if (editingAttributeId == null || machineConfig) { return; }
    GetRequest({
      url: `/quote-builder/user_unit_price_attributes/config/${machineKey}/${machineKey}`,
      callback: (data) => { setMachineConfig(data); },
    });
  }, [editingAttributeId, machineConfig, machineKey]);

  if (adding) {
    return (
      <MachineEntryStep
        machineKey={machineKey}
        allowedTypes={allowedTypes}
        libraryAttributeIds={libraryAttributeIds}
        onMachineAdded={onResolved}
      />
    );
  }

  if (editingAttributeId != null) {
    if (!machineConfig) { return <div className="library-picker__status">{t('quote_builder.please_wait')}</div>; }
    return (
      <QuoteBuilderForm
        config={machineConfig.create_sub_form}
        showingState="edit"
        editStateId={editingAttributeId}
        editedKlass="UserUnitPriceAttribute"
        linkToSettings={false}
        afterSave={onResolved}
        afterCancel={() => setEditingAttributeId(null)}
      />
    );
  }

  return (
    <div className="dct-empty-state" data-machine-thickness-mismatch>
      <div className="dct-empty-state__text">
        {t(`setup_wizard.${machineKey}_thickness_mismatch`, { thickness: formatThicknessMm(materialThicknessMm) })}
      </div>
      {machines.map((machine) => (
        <div key={machine.attribute_id} className="dct-empty-state__text">
          <strong>{machine.name}</strong>
          {machine.material_thickness_formatted ? ` · ${machine.material_thickness_formatted}` : ''}
        </div>
      ))}
      <div className="dct-empty-state__action">
        <button type="button" className="dct-button dct-button--primary" onClick={() => setAdding(true)}>
          {t('setup_wizard.add_new_machine')}
        </button>
        {machines.map((machine) => (
          <button
            key={machine.attribute_id}
            type="button"
            className="dct-button dct-button--secondary"
            style={{ marginLeft: '8px' }}
            onClick={() => setEditingAttributeId(machine.attribute_id)}
          >
            {t('setup_wizard.edit_machine', { machine: machine.name })}
          </button>
        ))}
      </div>
    </div>
  );
};
